import { IdResourceCollectionStore, IdResourceCollectionStoreConfig } from '~/lib/store/ResourceCollectionStore';
import { Action } from '~/lib/ApiModel';
import { LoadStatus } from '~/lib/store/LoadingTracker';
import { computed, IValueDidChange, Lambda, makeObservable, observe, override } from 'mobx';
import { exclude, orderBy } from '~/lib/helper/ArrayHelper';
import { EventEmitter, EventListener } from '~/lib/helper/EventHelper';
import { IApiClient } from '../ApiClient';

const statusActions = ['ping', 'uptime', 'update_network'];

export class ActionStore extends IdResourceCollectionStore<Action> {
    actionRefresher?: ReturnType<typeof setInterval> = undefined;
    private refreshing = false;

    actionCompleteListeners: EventEmitter<Action> = new EventEmitter();
    actionCompleteObserverDestructor?: Lambda = undefined;

    // Tracked here rather than read from `status`, which is still Loading when the first load's items apply.
    private itemsLoaded = false;
    private readonly addedBeforeLoad = new Set<number>();

    constructor(
        public apiClient: IApiClient,
        config: IdResourceCollectionStoreConfig<Action>
    ) {
        super(config);
        makeObservable(this);
    }

    stopRefreshingActions() {
        this.refreshing = false;
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        this.pauseRefreshing();
    }

    @computed
    get monitoredActions(): Action[] {
        return this.items.filter((a) => statusActions.indexOf(a.type) < 0);
    }

    @computed
    get actionsFinished(): Action[] {
        return orderBy(
            this.monitoredActions.filter((a) => a.status === 'completed' || a.status === 'errored'),
            (a) => a.completed_at
        );
    }

    @computed
    get actionsPending(): Action[] {
        return orderBy(
            this.monitoredActions.filter((a) => a.status === 'in-progress'),
            (a) => a.started_at
        );
    }

    @computed
    get orderedActions(): Action[] {
        return this.actionsPending.concat(this.actionsFinished);
    }

    @computed
    get mostRecentAction(): Action | undefined {
        const actions = this.orderedActions;
        if (actions.length > 0) {
            return actions[0];
        }
        return undefined;
    }

    @override
    setItems(items: Action[]): Action[] {
        this.itemsLoaded = true;
        return super.setItems(items);
    }

    @override
    addItem(item: Action): Action {
        if (!this.itemsLoaded) this.addedBeforeLoad.add(item.id);
        return super.addItem(item);
    }

    addAndReloadIfExists(action: Action | undefined): Action | undefined {
        if (action !== undefined) {
            action = this.addItem(action); // add our action to the collection and assign the new observable version to the action variable
            void this.load(); // force a reload to get the latest status from the server instead of waiting for auto refresh polling.
        }
        return action;
    }

    private async autoRefreshActions() {
        // We only refresh our actions if there are any actions in our collection that are not complete.
        // it is expected that the Action returned by a post to server/{id}/actions will be added to items using addItem()
        // this saves making many calls to the server when the user is idle
        if (this.status === LoadStatus.Complete && this.items.filter((a) => a.status === 'in-progress').length === 0)
            return;
        // Skip while a load is in flight: a newer load supersedes it, so a poll slower than the
        // interval would otherwise never update items. A hung request pauses polling until it fails.
        if (this.status === LoadStatus.Loading) return;
        await this.load();
    }

    startRefreshingActions() {
        if (this.refreshing) return;
        this.refreshing = true;
        // Polling a hidden tab issues requests whose results nobody sees, so the interval only
        // runs while the tab is visible.
        document.addEventListener('visibilitychange', this.onVisibilityChange);
        if (document.hidden) {
            void this.autoRefreshActions();
        } else {
            this.resumeRefreshing();
        }
    }

    private readonly onVisibilityChange = () => {
        if (document.hidden) {
            this.pauseRefreshing();
        } else {
            this.resumeRefreshing();
        }
    };

    private resumeRefreshing() {
        if (!this.actionRefresher) {
            void this.autoRefreshActions();
            this.actionRefresher = setInterval(() => this.autoRefreshActions(), 1500);
        }
    }

    private pauseRefreshing() {
        if (this.actionRefresher) {
            clearInterval(this.actionRefresher);
            this.actionRefresher = undefined;
        }
    }

    proceedWithAction(action: Action) {
        return this.apiClient.actionProceed(action.id, true);
    }
    abortAction(action: Action) {
        return this.apiClient.actionProceed(action.id, false);
    }

    onActionComplete(listener: EventListener<Action>) {
        //our first listener starts the observer
        if (!this.actionCompleteObserverDestructor) {
            // Until the first load applies, report only actions added via `addItem`; any other
            // finished action it returns predates the page.
            let hasDoneInitialLoad = this.itemsLoaded;
            this.actionCompleteObserverDestructor = observe(this, 'actionsFinished', (c) => {
                if (hasDoneInitialLoad) {
                    this.actionsFinishedChanged(c);
                } else {
                    this.actionsFinishedChanged(c, (a) => this.addedBeforeLoad.has(a.id));
                    hasDoneInitialLoad = this.itemsLoaded;
                }
            });
        }

        const off = this.actionCompleteListeners.on(listener);

        return () => {
            //stop listening
            off();

            //remove our observer if we are the last
            if (this.actionCompleteListeners.listeners.length === 0) {
                this.actionCompleteObserverDestructor?.();
            }
        };
    }

    private actionsFinishedChanged(c: IValueDidChange<Action[]>, include: (a: Action) => boolean = () => true) {
        const added = exclude(c.newValue, c.oldValue as Action[], (a) => a.id);
        if (added.length > 0) {
            for (const finished of added) {
                if ((finished.status === 'completed' || finished.status === 'errored') && include(finished)) {
                    this.actionCompleteListeners.fire(finished);
                }
            }
        }
    }
}
